import axios from 'axios';
import Cookies from 'js-cookie';
import { beginGlobalRequest, endGlobalRequest } from './requestTracker';


const normalizedBaseURL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const axiosInstance = axios.create({
  baseURL: normalizedBaseURL,
});

const LOADER_TRACKING_KEY = '__globalLoaderTracked';

const shouldTrackRequest = (config = {}) => !config.skipGlobalLoader;

const startTrackedRequest = (config = {}) => {
  if (!shouldTrackRequest(config) || config[LOADER_TRACKING_KEY]) {
    return config;
  }

  config[LOADER_TRACKING_KEY] = true;
  beginGlobalRequest();
  return config;
};

const finishTrackedRequest = (config = {}) => {
  if (!config?.[LOADER_TRACKING_KEY]) {
    return;
  }

  config[LOADER_TRACKING_KEY] = false;
  endGlobalRequest();
};

const attachLoaderInterceptors = (client) => {
  client.interceptors.request.use(
    (config) => startTrackedRequest(config),
    (error) => {
      finishTrackedRequest(error?.config);
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => {
      finishTrackedRequest(response?.config);
      return response;
    },
    (error) => {
      finishTrackedRequest(error?.config);
      return Promise.reject(error);
    }
  );
};

attachLoaderInterceptors(axios);
attachLoaderInterceptors(axiosInstance);

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (typeof config.url === 'string') {
      config.url = config.url.replace(/^\/+/, '');
    }

    if (token && !config.url?.includes('/auth/')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response) {
      const status = error.response.status;

      if (status === 401 && !originalRequest._retry) {
        // Prevent infinite loops
        originalRequest._retry = true;

        try {
          // Get the refresh token from cookies
          const refreshToken = Cookies.get('refreshToken');


          const response = await axios.post(`${normalizedBaseURL}/admin/refresh`, null, {
            withCredentials: true,
            token: refreshToken,
            skipGlobalLoader: true
          });

          const newToken = response.data?.access_token;

          if (newToken) {
            localStorage.setItem('token', newToken);

            axiosInstance.defaults.headers['Authorization'] = `Bearer ${newToken}`;

            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
            return Promise.reject(error);
          }
        } catch (refreshError) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        return Promise.reject(error);
      }
    } else {
      return Promise.reject(error);
    }
  }
);

export default axiosInstance;
